import type { FC, ReactNode } from 'react'
import type { Wallpaper } from '@/types'

import { useId, useState } from 'react'
import Image from 'next/image'
import { Maximize2, Monitor, Smartphone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toCssColor } from '@/lib/colors'
import { getAspectRatio } from '@/lib/format'

type PreviewMode = 'full' | 'desktop' | 'phone'

interface WallpaperPreviewProps {
    wallpaper: Wallpaper
}

const MODES: Array<{ value: PreviewMode, label: string, icon: ReactNode }> = [
    { value: 'full', label: 'Full image', icon: <Maximize2 aria-hidden='true' className='size-4' /> },
    { value: 'desktop', label: 'On a desktop', icon: <Monitor aria-hidden='true' className='size-4' /> },
    { value: 'phone', label: 'On a phone', icon: <Smartphone aria-hidden='true' className='size-4' /> }
]

// Shows the wallpaper as-is, or dropped into a desktop or phone frame to judge how it will look.
export const WallpaperPreview: FC<WallpaperPreviewProps> = ({ wallpaper }): ReactNode => {
    const [mode, setMode] = useState<PreviewMode>('full')
    const baseId = useId()
    const ratio = getAspectRatio(wallpaper)
    const alt = wallpaper.image_alt || wallpaper.title

    return (
        <div>
            <div role='tablist' aria-label='Preview' className='inline-flex flex-wrap gap-1 rounded-xl border border-white/10 bg-white/[0.03] p-1'>
                {MODES.map(option => {
                    const selected = mode === option.value

                    return (
                        <button
                            key={option.value}
                            type='button'
                            role='tab'
                            id={`${baseId}-tab-${option.value}`}
                            aria-selected={selected}
                            aria-controls={`${baseId}-panel`}
                            onClick={() => setMode(option.value)}
                            className={cn(
                                'inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                                selected ? 'bg-white/[0.12] text-foreground' : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {option.icon}
                            {option.label}
                        </button>
                    )
                })}
            </div>

            <div
                id={`${baseId}-panel`}
                role='tabpanel'
                aria-labelledby={`${baseId}-tab-${mode}`}
                className='mt-4 flex min-h-[18rem] items-center justify-center overflow-hidden rounded-3xl bg-surface p-4 ring-1 ring-white/[0.06] sm:p-8'
            >
                {mode === 'full' && (
                    <div
                        className='relative w-full overflow-hidden rounded-2xl'
                        style={{ aspectRatio: ratio, maxWidth: `min(100%, calc(72vh * ${ratio}))`, backgroundColor: toCssColor(wallpaper.color) }}
                    >
                        <Image
                            src={wallpaper.image}
                            alt={alt}
                            fill
                            preload
                            sizes='(min-width: 1024px) 62vw, 100vw'
                            className='object-cover'
                        />
                    </div>
                )}

                {mode === 'desktop' && (
                    <figure className='w-full max-w-3xl'>
                        <div className='rounded-[1.4rem] bg-neutral-950 p-2.5 shadow-2xl ring-1 ring-white/10 sm:p-3'>
                            <div className='relative aspect-[16/10] overflow-hidden rounded-xl' style={{ backgroundColor: toCssColor(wallpaper.color) }}>
                                <Image
                                    src={wallpaper.image}
                                    alt={alt}
                                    fill
                                    sizes='(min-width: 1024px) 48rem, 100vw'
                                    className='object-cover'
                                />

                                <div aria-hidden='true' className='absolute inset-x-0 top-0 flex h-6 items-center gap-3 bg-black/25 px-3 backdrop-blur-md'>
                                    <span className='size-2 rounded-full bg-white/70' />
                                    <span className='h-1.5 w-8 rounded-full bg-white/50' />
                                    <span className='h-1.5 w-6 rounded-full bg-white/40' />
                                    <span className='ml-auto h-1.5 w-10 rounded-full bg-white/50' />
                                </div>

                                <div aria-hidden='true' className='absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-xl bg-white/20 p-1.5 backdrop-blur-md'>
                                    {['#f6b65e', '#7c6cf0', '#f08aa8', '#5fc4b5', '#e9e5de'].map(swatch => (
                                        <span key={swatch} className='size-5 rounded-md sm:size-7 sm:rounded-lg' style={{ backgroundColor: swatch }} />
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div aria-hidden='true' className='mx-auto h-8 w-20 bg-linear-to-b from-neutral-800 to-neutral-900 sm:h-10' />
                        <div aria-hidden='true' className='mx-auto h-2 w-44 rounded-full bg-neutral-800' />
                        <figcaption className='sr-only'>The wallpaper on a desktop screen</figcaption>
                    </figure>
                )}

                {mode === 'phone' && (
                    <figure className='w-full max-w-[17rem]'>
                        <div className='rounded-[2.75rem] bg-neutral-950 p-2.5 shadow-2xl ring-1 ring-white/15'>
                            <div className='relative aspect-[9/19.5] overflow-hidden rounded-[2.25rem]' style={{ backgroundColor: toCssColor(wallpaper.color) }}>
                                <Image
                                    src={wallpaper.image}
                                    alt={alt}
                                    fill
                                    sizes='17rem'
                                    className='object-cover'
                                />

                                <div aria-hidden='true' className='absolute top-2.5 left-1/2 h-6 w-20 -translate-x-1/2 rounded-full bg-black' />

                                <div aria-hidden='true' className='absolute inset-x-0 top-14 text-center text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]'>
                                    <p className='text-sm font-medium opacity-90'>
                                        Monday
                                    </p>
                                    <p className='text-6xl font-semibold tracking-tight'>
                                        9:41
                                    </p>
                                </div>

                                <div aria-hidden='true' className='absolute inset-x-0 bottom-3 flex justify-center'>
                                    <span className='h-1 w-24 rounded-full bg-white/80' />
                                </div>
                            </div>
                        </div>

                        <figcaption className='sr-only'>The wallpaper on a phone lock screen</figcaption>
                    </figure>
                )}
            </div>
        </div>
    )
}
